import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Activity, Ticket, Server, Boxes, Network, ShieldCheck, ShieldAlert,
  FileText, KeyRound, FileSignature, Cpu, HardDriveDownload, BarChart3, Sparkles,
  Building2, Users, Repeat, TerminalSquare, Search, Bell, ChevronDown, LogOut, Wand2, GanttChart, Rocket, Database, Package,
  BellRing, Clock, Timer, SlidersHorizontal, BookOpen, AlertOctagon, Mail, Smile, ListChecks,
  Receipt, CalendarClock, CircleDollarSign
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const NAV = [
  {
    group: "Command Center",
    items: [
      { to: "/", label: "Executive", icon: LayoutDashboard, testid: "nav-executive" },
      { to: "/exec/security", label: "Security Command · CTO", icon: ShieldAlert, testid: "nav-exec-security" },
      { to: "/exec/compliance", label: "Compliance Command · CEO", icon: ShieldCheck, testid: "nav-exec-compliance" },
      { to: "/alerts", label: "Alerts", icon: BellRing, testid: "nav-alerts" },
      { to: "/rmm", label: "RMM Operations", icon: Activity, testid: "nav-rmm" },
    ],
  },
  {
    group: "Service Desk",
    items: [
      { to: "/tickets", label: "Tickets", icon: Ticket, testid: "nav-tickets" },
      { to: "/problems", label: "Problems", icon: AlertOctagon, testid: "nav-problems" },
      // Guard the write, hide the button too: the backend already refuses a
      // client-portal caller by DEFAULT (authz.client_scope_guard -- this
      // route is in neither _PUBLIC nor PORTAL_ALLOW), but a nav entry a
      // portal account could still click through to a 403 is a worse UX for
      // a door that was never open. Scoped to authz.STAFF_ROLES verbatim.
      { to: "/csat", label: "CSAT Report", icon: Smile, testid: "nav-csat",
        roles: ["super_admin", "msp_owner", "service_desk_manager",
                "l1_technician", "l2_technician", "l3_technician",
                "security_analyst", "compliance_officer", "auditor"] },
      { to: "/time-approval", label: "Time Approval", icon: Timer, testid: "nav-time-approval",
        roles: ["service_desk_manager", "l3_technician", "msp_owner", "super_admin"] },
      { to: "/service-desk", label: "Service Desk Config", icon: SlidersHorizontal, testid: "nav-service-desk",
        roles: ["service_desk_manager", "l3_technician", "msp_owner", "super_admin"] },
      // Guard the write, hide the button too: the backend already refuses
      // anyone outside `ticket_fields.MANAGE` with 403 (`require_roles`
      // also admits super_admin/msp_owner) -- this entry is scoped to
      // exactly that set so nobody else is invited to click through to one.
      { to: "/settings/ticket-fields", label: "Ticket Field Settings", icon: ListChecks,
        testid: "nav-ticket-fields",
        roles: ["service_desk_manager", "l3_technician", "msp_owner", "super_admin"] },
      // Task 9 -- same reasoning as the entry above: the backend already
      // refuses anyone outside `approvals.MANAGE` with 403, this entry is
      // scoped to exactly that set so nobody else is invited to click
      // through to one.
      { to: "/settings/ticket-approvals", label: "Approval Rules", icon: ShieldCheck,
        testid: "nav-ticket-approvals",
        roles: ["service_desk_manager", "l3_technician", "msp_owner", "super_admin"] },
      // Mail triage is an l1/l2 job and the backend authorises them for every
      // quarantine route, but the Mail tab lives inside the page above -- whose
      // only link is manager-and-l3. So the two roles the feature was built for
      // had no way in but typing the URL. This is a SEPARATE entry rather than
      // four more roles on that one, because the page also holds SLA policies
      // and the work-type rate card, and widening the link would have handed
      // l1 those too. ServiceDesk.jsx gates its config tabs to match.
      { to: "/service-desk?tab=mail", label: "Mail", icon: Mail, testid: "nav-desk-mail",
        roles: ["service_desk_manager", "l1_technician", "l2_technician",
                "l3_technician", "msp_owner", "super_admin"] },
    ],
  },
  {
    // Billing PREPARATION, not invoicing: nothing here issues a numbered
    // document. Guard the write, hide the button too -- every route behind
    // these three pages is `require_roles(*billing.MANAGE)`, which
    // `core.require_roles` also admits super_admin and msp_owner to, and a
    // client-portal account is refused by `client_scope_guard` before any
    // handler runs. This list is exactly that set, so nobody else is invited
    // to click through to a 403. The server is the lock; this is the door.
    group: "Billing",
    items: [
      { to: "/billing/unbilled", label: "Unbilled Work", icon: CalendarClock, testid: "nav-unbilled",
        roles: ["service_desk_manager", "l3_technician", "msp_owner", "super_admin"] },
      { to: "/billing/runs", label: "Billing Runs", icon: Receipt, testid: "nav-billing-runs",
        roles: ["service_desk_manager", "l3_technician", "msp_owner", "super_admin"] },
      { to: "/billing/charges", label: "Contract Charges", icon: CircleDollarSign, testid: "nav-contract-charges",
        roles: ["service_desk_manager", "l3_technician", "msp_owner", "super_admin"] },
    ],
  },
  {
    group: "Assets & Inventory",
    items: [
      { to: "/clients", label: "Clients", icon: Building2, testid: "nav-clients" },
      { to: "/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory" },
      { to: "/software", label: "Software", icon: Package, testid: "nav-software" },
      { to: "/network", label: "Network Discovery", icon: Network, testid: "nav-network" },
    ],
  },
  {
    group: "Compliance & Security",
    items: [
      { to: "/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance" },
      { to: "/security", label: "Security Ops", icon: ShieldAlert, testid: "nav-security" },
      { to: "/vulnerabilities", label: "Vulnerabilities", icon: Wand2, testid: "nav-vulns" },
    ],
  },
  {
    group: "Knowledge",
    items: [
      { to: "/docs", label: "Documentation", icon: FileText, testid: "nav-docs" },
      { to: "/knowledge", label: "Knowledge Base", icon: BookOpen, testid: "nav-knowledge" },
      { to: "/vault", label: "Credential Vault", icon: KeyRound, testid: "nav-vault" },
    ],
  },
  {
    group: "Governance",
    items: [
      { to: "/contracts", label: "Service Agreements", icon: FileSignature, testid: "nav-contracts" },
      { to: "/licenses", label: "Licenses", icon: Cpu, testid: "nav-licenses" },
      { to: "/backups", label: "Backups", icon: HardDriveDownload, testid: "nav-backups" },
    ],
  },
  {
    group: "Intelligence",
    items: [
      { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
      { to: "/exports", label: "Export Engine", icon: BarChart3, testid: "nav-exports" },
      { to: "/msp-bi", label: "Client Intelligence", icon: GanttChart, testid: "nav-msp-bi" },
      { to: "/ai", label: "AI Operations", icon: Sparkles, testid: "nav-ai" },
    ],
  },
  {
    group: "Identity & Integrations",
    items: [
      { to: "/identity", label: "AD · Entra · M365", icon: Building2, testid: "nav-identity" },
      { to: "/reconciliation", label: "Reconciliation", icon: Repeat, testid: "nav-recon" },
      { to: "/evidence", label: "Evidence Vault", icon: FileText, testid: "nav-evidence" },
      { to: "/email", label: "Email Engine", icon: FileText, testid: "nav-email" },
      { to: "/mfa", label: "MFA Settings", icon: ShieldCheck, testid: "nav-mfa" },
      { to: "/users", label: "User Management", icon: Users, testid: "nav-users" },
    ],
  },
  {
    group: "Endpoint Architecture",
    items: [
      { to: "/devices", label: "Devices", icon: Server, testid: "nav-devices" },
      { to: "/agents", label: "Agent Deployment", icon: TerminalSquare, testid: "nav-agents" },
      { to: "/installer", label: "Agent Installer · Give-out", icon: Rocket, testid: "nav-installer" },
      { to: "/demo", label: "Demo Data", icon: Database, testid: "nav-demo", demoOnly: true },
      { to: "/automation", label: "Automation", icon: Clock, testid: "nav-automation" },
      { to: "/remediation", label: "Auto Remediation", icon: Wand2, testid: "nav-remediation" },
      { to: "/recurring-v2", label: "Recurring 2.0", icon: Repeat, testid: "nav-rv2" },
      { to: "/portal", label: "Client Portal", icon: Building2, testid: "nav-portal" },
    ],
  },
];

// A client-portal account gets THIS nav, never the one above. `NAV`'s own
// per-item `roles` filter (below) is defence in depth for the rows that
// already carry a list, but most staff rows carry none at all -- "visible
// unless restricted" -- so a client-scoped role fell through to the full
// 35-item MSP console shell, every one of whose API calls 403s. Building the
// client nav as its own short list, rather than trying to retrofit `roles`
// onto every existing entry, is what makes "what does a customer see" a
// single array to read rather than an inference over thirty `!it.roles ||`
// checks.
const PORTAL_NAV = [
  {
    group: "Client Portal",
    items: [
      { to: "/portal", label: "Overview", icon: Building2, testid: "nav-portal-overview" },
      { to: "/portal/tickets", label: "Your tickets", icon: Ticket, testid: "nav-portal-tickets" },
      { to: "/portal/knowledge", label: "Knowledge base", icon: BookOpen, testid: "nav-portal-knowledge" },
    ],
  },
];

// Mirrors backend/authz.py's CLIENT_SCOPED_ROLES (also copied, for the same
// reason, in App.js/PortalTickets.jsx/NewTicketDialog.jsx). COPY ONLY: it
// picks which nav array RENDERS, never what the API answers.
const CLIENT_SCOPED_ROLES = [
  "client_read_only", "client_admin", "client_administrator",
  "client_manager", "client_user", "client_contact",
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [tenantName, setTenantName] = useState("");
  // Demo Data is a pre-sale tool. When the deployment turns it off the route
  // refuses, so the menu entry must go too rather than lead to a 410.
  const [demoTools, setDemoTools] = useState(false);
  const isClientScoped = CLIENT_SCOPED_ROLES.includes(user?.role);
  const navGroups = isClientScoped ? PORTAL_NAV : NAV;
  useEffect(() => { api.get("/tenant").then(r => setTenantName(r.data?.name || "")).catch(() => {}); }, []);
  // Demo Data is staff-only and absent from PORTAL_NAV regardless -- skip the
  // call for a client-scoped session rather than let it 403 for no reason.
  useEffect(() => {
    if (isClientScoped) return;
    api.get("/demo/status").then(r => setDemoTools(r.data?.enabled !== false)).catch(() => setDemoTools(false));
  }, [isClientScoped]);
  const initials = (user?.name || "MS").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 flex">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-[#0a0a0c] border-r border-zinc-900 fixed left-0 top-0 h-screen flex flex-col" data-testid="app-sidebar">
        <div className="px-5 h-14 flex items-center border-b border-zinc-900">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center font-mono font-bold text-white">A</div>
          <div className="ml-3">
            <div className="font-display font-semibold text-[15px] tracking-tight">AegisOne RMM</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 -mt-0.5">MSPOS · Command</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {navGroups.map((g) => (
            <div key={g.group} className="mb-4">
              <div className="px-3 mb-1 text-[10px] uppercase tracking-[0.16em] text-zinc-500">{g.group}</div>
              {g.items
                .filter((it) => !it.demoOnly || demoTools)
                .filter((it) => !it.roles || it.roles.includes(user?.role))
                .map((it) => {
                const active = loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to));
                const Icon = it.icon;
                return (
                  <Link
                    to={it.to}
                    key={it.to}
                    data-testid={it.testid}
                    className={`flex items-center gap-3 px-3 py-1.5 mx-1 rounded-md text-[13px] transition-colors ${active ? "bg-blue-600/15 text-zinc-50 border border-blue-600/30" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60 border border-transparent"}`}
                  >
                    <Icon size={15} />
                    <span>{it.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-zinc-900 text-[11px] text-zinc-500 font-mono">
          <div className="flex items-center justify-between">
            <span>v1.0.0</span>
            <span className="text-emerald-500">● online</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 min-h-screen">
        {/* Topbar */}
        <header className="h-14 sticky top-0 z-30 bg-[#09090B]/85 backdrop-blur-md border-b border-zinc-900 flex items-center px-6 gap-4">
          <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono uppercase tracking-widest">
            <span>Tenant</span>
            <span className="text-zinc-200 normal-case" data-testid="topbar-tenant">{tenantName || "—"}</span>
            <ChevronDown size={12} />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="w-full max-w-xl relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && q) nav(`/search?q=${encodeURIComponent(q)}`); }}
                placeholder="Search assets, tickets, clients, documents…"
                data-testid="global-search"
                className="w-full bg-zinc-900/70 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/60"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-zinc-600 border border-zinc-800 rounded px-1 py-0.5">⌘K</span>
            </div>
          </div>
          <button data-testid="topbar-notifications" className="p-1.5 hover:bg-zinc-900 rounded-md text-zinc-400 relative">
            <Bell size={16} />
            <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="flex items-center gap-2 hover:bg-zinc-900 rounded-md px-2 py-1">
                <Avatar className="h-7 w-7"><AvatarFallback className="text-[10px] bg-blue-600/30 text-blue-300">{initials}</AvatarFallback></Avatar>
                <div className="text-left">
                  <div className="text-xs font-medium leading-tight">{user?.name || "Operator"}</div>
                  <div className="text-[10px] text-zinc-500 leading-tight uppercase tracking-widest">{user?.role?.replaceAll("_", " ")}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-950 border-zinc-800 text-zinc-200">
              <DropdownMenuItem className="text-xs">{user?.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn">
                <LogOut size={14} className="mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-6 fade-up">{children}</main>
      </div>
    </div>
  );
}
